import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';

import { BaseService } from './base-service';
import { ConfirmationService } from './confirmation.service';
import { GroupsService } from './groups.service';
import { MembershipsService, isProjectMembership } from './membership.service';
import { RolesService } from './role.service';
import { UIProjectGroupsRefreshService } from './ui-project-groups-refresh.service';
import { UIProjectUsersRefreshService } from './ui-project-users-refresh.service';
import { UsersService } from './users.service';
import { BaseProjectGroupDto, BaseProjectGroupDtoPage } from '@api/backend-connector/models';
import { ProjectsApi } from '@api/backend-connector/services';

export const canProjectGroupBeRemoved = (group: BaseProjectGroupDto) => {
  return group.id && !group.memberships.find((m) => !isProjectMembership(group.id, m));
};

export const canRemoveUserGroupMemberships = (group: BaseProjectGroupDto) => {
  // when the group has both project and global/group permissions
  return !canProjectGroupBeRemoved(group) && group.memberships.find((m) => isProjectMembership(group.id, m));
};

const toasts = {
  createSuccessSummary: 'i18n.UIProjectGroupsToast.createSuccessSummary',
  createSuccessDetail: 'i18n.UIProjectGroupsToast.createSuccessDetail',
  createFailSummary: 'i18n.UIProjectGroupsToast.createFailSummary',
  createFailDetail: 'i18n.UIProjectGroupsToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.UIProjectGroupsToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.UIProjectGroupsToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.UIProjectGroupsToast.deleteFailSummary',
  deleteFailDetail: 'i18n.UIProjectGroupsToast.deleteFailDetail',
};

const confirmMessages = {
  removeGroupHeader: 'i18n.UIProjectGroup.ConfirmRemoveGroup.header',
  removeGroupMessage: 'i18n.UIProjectGroup.ConfirmRemoveGroup.message',
  removeGroupProjectMembershipsHeader: 'i18n.UIProjectGroup.ConfirmRemoveGroupProjectMemberships.header',
  removeGroupProjectMembershipsMessage: 'i18n.UIProjectGroup.ConfirmRemoveGroupProjectMemberships.message',
};

@Injectable({
  providedIn: 'root',
})
export class UIProjectGroupsService extends BaseService {
  override toasts = toasts;

  private readonly projectsApi = inject(ProjectsApi);
  private readonly rolesService = inject(RolesService);
  private readonly membershipsService = inject(MembershipsService);
  private readonly groupsService = inject(GroupsService);
  private readonly usersService = inject(UsersService);
  private readonly uiProjectGroupsRefreshService = inject(UIProjectGroupsRefreshService);
  private readonly uiProjectUsersRefreshService = inject(UIProjectUsersRefreshService);

  private refresh() {
    this.usersService.refresh$.next(); // maybe not
    this.groupsService.refresh$.next(); // maybe not
    this.uiProjectGroupsRefreshService.refresh$.next();
    this.uiProjectUsersRefreshService.refresh$.next();
  }

  listProjectGroups(
    params: Parameters<typeof UIProjectGroupsService.prototype.projectsApi.listProjectGroups>[0],
  ): Observable<BaseProjectGroupDtoPage> {
    return this.projectsApi.listProjectGroups(params);
  }

  isProjectGroup(params: { projectId: string; memberId: string }) {
    return this.listProjectGroups({ id: params.projectId, memberId: params.memberId }).pipe(
      map((result) => !!result.count),
    );
  }

  async addMember(params: { projectId: string; memberId: string }) {
    const isProjectMember = await firstValueFrom(this.isProjectGroup(params));
    if (isProjectMember) {
      throw new Error();
    }

    const allDefaultRoles = await firstValueFrom(this.rolesService.getAllDefaultRoles());
    if (!allDefaultRoles.length) {
      throw new Error();
    }
    await Promise.all(
      allDefaultRoles.map((role) =>
        firstValueFrom(this.membershipsService.createMembership2({ ...params, roleId: role.id })),
      ),
    );
    this.refresh();
  }

  async removeGroup(params: { projectId: string; projectGroup: BaseProjectGroupDto }) {
    await Promise.all(
      params.projectGroup.memberships
        .filter((m) => isProjectMembership(params.projectGroup.id, m))
        .map((membership) => firstValueFrom(this.membershipsService.deleteMembership2({ id: membership.id }))),
    );
    this.refresh();
  }

  async openRemoveGroupConfirmationDialog(confirmationService: ConfirmationService, label: string) {
    return confirmationService.confirm({
      header: this.translateService.instant(confirmMessages.removeGroupHeader),
      message: this.translateService.instant(confirmMessages.removeGroupMessage, { label }),
      confirmLabel: this.translateService.instant(this.confirmDelete.confirmLabel),
    });
  }

  async openRemoveGroupProjectMembershipsConfirmationDialog(confirmationService: ConfirmationService, label: string) {
    return confirmationService.confirm({
      header: this.translateService.instant(confirmMessages.removeGroupProjectMembershipsHeader),
      message: this.translateService.instant(confirmMessages.removeGroupProjectMembershipsMessage, { label }),
      confirmLabel: this.translateService.instant(this.confirmDelete.confirmLabel),
    });
  }
}
